/**
 * Die Läufe des Kontos.
 *
 * WARUM DIE DATEN AUS EINER FUNCTION KOMMEN: `sim_runs` hat bewusst keine
 * RLS-Richtlinie. In `result` liegt der geprüfte Kundentext samt jeder
 * Persona-Reaktion; die Tabelle für den Browser zu öffnen wäre der schnellere
 * Weg und der schlechtere. `GET /billing/runs` gibt nur die Spalten heraus, die
 * eine Liste braucht — dieselbe Begründung wie beim Guthaben.
 *
 * WAS HIER NICHT STEHT: der Text eines Laufs und die einzelnen Einwände. Die
 * stehen seit dem 26.09.2026 in der Detailansicht (/runs/:id, LaufDetail.tsx),
 * die jede Zeile verlinkt. Diese Liste beantwortet „was habe ich laufen lassen
 * und was hat es gekostet", die Detailansicht „was kam heraus".
 *
 * SEIT 29.09.2026 GRUPPIERT (Robert: „der screen ist nicht so cool", Entwurf
 * 1b mit der Skala aus 1a, Claude-Design-Projekt `7f590d08-…`, `Runs v2`).
 * Runs desselben Tags, derselben Studie, Zielgruppe und Textart stehen in
 * einer Karte, mit Verlauf F1, F2, … und je Zeile einer Score-Skala statt
 * „Score 6,6 zu 5,3". Die Regel steht in `runs-gruppen.ts`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiFehler, nochmalSinnvoll, ladeBerichte, ladeLaeufe, ladeQuote, ladeStatus, setzeLeser, zieheLinkZurueck, type BerichtEintrag, type Lauf, type Quote } from '../api';
import ErgebnisMelden from './ErgebnisMelden';
import { textart } from './LaufDetail';
import { useSitzung } from '../session';
import { ersterLaufVorschlag } from '../erster-lauf';
import { istVerbunden } from '../verbindung';
import { meldeVerbindung } from '../messung';
import { bekannteAntworten } from './Fragen';
import type { Trefferquoten } from '../api';
import { Abschnitt, AppRahmen, ErsterLauf, Hinweis, KleinerKnopf, Marke, Panel, Platzhalter, Seitenkopf, Zeile } from '../ui';
import { useNeuLadenBeimZurueckkehren } from '../neu-laden';
import { aufSkala, gruppiere, tagText, type RunGruppe } from '../runs-gruppen';
import { filtere, gueltigeWahl, projekteAus, studienOhneProjekt, wahlAlsParam, wahlAusParam, type ProjektWahl } from '../runs-projekte';
import { ProjekteZuordnen, ProjektNavigation, RunSuche } from '../runs-projekt-filter';
import { fassungsname } from '../fassungsname';
import { locale, sprache, t } from '../sprache';

const URTEIL: Record<string, { text: string; art: 'gut' | 'schlecht' | 'neutral' }> = {
  candidate: { text: 'gewinnt', art: 'gut' },
  baseline: { text: 'verliert', art: 'schlecht' },
  tie: { text: 'unentschieden', art: 'neutral' },
};
const URTEIL_EN: typeof URTEIL = {
  candidate: { text: 'wins', art: 'gut' },
  baseline: { text: 'loses', art: 'schlecht' },
  tie: { text: 'undecided', art: 'neutral' },
};

/**
 * Das Urteil eines Laufs — dasselbe, das Claude nach dem Lauf gesagt hat.
 *
 * Bei mode 'feedback' lief kein Vergleich. `verdict` steht dort auf 'tie',
 * weil die Spalte Pflicht ist — das als „unentschieden" anzuzeigen waere eine
 * erfundene Aussage.
 *
 * `knapp` kommt vom Server und folgt der Rauschschwelle aus `render.ts`. Bis
 * zum 25.09.2026 zeigte diese Seite `verdict` direkt, also „gewinnt" auch bei
 * 4:2 — ein Sieg, den Claude im selben Lauf „zu knapp fuer ein Urteil" nannte.
 */
function urteilVon(l: Lauf): { text: string; art: 'gut' | 'schlecht' | 'neutral' } | null {
  const u = (sprache() === 'en' ? URTEIL_EN : URTEIL)[l.verdict];
  if (l.mode !== 'compare' || !u) return null;
  return l.knapp ? { text: t('zu knapp', 'too close'), art: 'neutral' } : u;
}

function Urteil({ l }: { l: Lauf }) {
  const u = urteilVon(l);
  if (l.mode === 'vorauswahl') return <span className="text-[0.8125rem] text-text-hell">{t('sortiert, keine Messung', 'sorted, no measurement')}</span>;
  if (!u) return <span className="text-[0.8125rem] text-text-hell">{t('kein Vergleich', 'no comparison')}</span>;
  return (
    <span className="inline-flex items-center gap-2 sm:flex-col sm:items-start sm:gap-1">
      <Marke art={u.art}>{u.text}</Marke>
      {l.stimmen && (
        <span className="font-serif text-[1.125rem] leading-none font-semibold tabular-nums text-tinte sm:text-[1.375rem]" title={t('Stimmen: deine Fassung zu Vergleich', 'Votes: your version to baseline')}>
          {l.stimmen.candidate}:{l.stimmen.baseline}
        </span>
      )}
    </span>
  );
}

/**
 * Beide Scores auf einer Bahn von 0 bis 10: orange die geprüfte Fassung, hohl
 * die Vergleichsfassung, dazwischen der Abstand in der Farbe des Urteils.
 * Ersetzt „Score 6,6 zu 5,3", das man lesen musste statt sehen.
 */
function Skala({ l }: { l: Lauf }) {
  if (!l.scores) return null;
  const neu = aufSkala(l.scores.candidate);
  const alt = aufSkala(l.scores.baseline);
  const art = urteilVon(l)?.art ?? 'neutral';
  const nah = Math.abs(neu - alt) < 4;
  const abstand = art === 'gut' ? 'bg-gut/45' : art === 'schlecht' ? 'bg-schlecht/45' : 'bg-linie-stark';
  return (
    <div>
      <div className="relative h-5" role="img" aria-label={t(`Score deine Fassung ${zahl(l.scores.candidate)}, Vergleich ${zahl(l.scores.baseline)}, Skala 0 bis 10`, `Score your version ${zahl(l.scores.candidate)}, baseline ${zahl(l.scores.baseline)}, scale 0 to 10`)}>
        <div className="absolute inset-x-0 top-[9px] h-0.5 rounded-full bg-linie-zart" />
        <div className={`absolute top-2 h-1 rounded-full ${abstand}`} style={{ left: `${Math.min(neu, alt)}%`, width: `${Math.abs(neu - alt)}%` }} />
        {!nah && <div className="absolute top-1 -ml-1.5 size-3 rounded-full border-2 border-text-leise bg-flaeche" style={{ left: `${alt}%` }} />}
        <div className="absolute top-1 -ml-1.5 size-3 rounded-full bg-marke ring-2 ring-flaeche" style={{ left: `${neu}%` }} />
        {/* Fast gleiche Scores (5,3 zu 5,4): der hohle Ring liegt durchsichtig obenauf, sonst verschwindet er unter dem orangen Punkt. */}
        {nah && <div className="absolute top-0.5 -ml-2 size-4 rounded-full border-2 border-text-leise" style={{ left: `${alt}%` }} />}
      </div>
      <div className="flex justify-between gap-2 text-[0.75rem] tabular-nums text-text-hell">
        <span><span className="font-semibold text-marke-tief">{zahl(l.scores.candidate)}</span> {t('deine', 'yours')}</span>
        <span>{zahl(l.scores.baseline)} {t('Vergleich', 'baseline')}</span>
      </div>
      {!l.knapp && l.score_im_rauschen && <div className="mt-0.5 text-[0.75rem] text-text-hell">{t('Stimmen klar, Punktabstand gering', 'Votes clear, points margin small')}</div>}
    </div>
  );
}

/**
 * Was im Markt passiert ist. Nur bei Vergleichen: ein Lauf ohne
 * Vergleichsfassung hat kein Urteil, gegen das sich ein Ergebnis prüfen ließe.
 * Offen steht nur ein leiser Link: der laute Knopf sitzt einmal oben im
 * Kasten, statt in jeder Zeile gleich laut (Robert, 29.09.2026).
 */
function Markt({ l, melden }: { l: Lauf; melden: (l: Lauf) => void }) {
  if (l.mode !== 'compare') return null;
  const e = l.ergebnis;
  const leise = 'cursor-pointer border-0 bg-transparent p-0 text-[0.8125rem] text-text-hell underline underline-offset-[3px] hover:text-marke-tief';
  if (!e) return <button type="button" onClick={() => melden(l)} className={`${leise} whitespace-nowrap`}>{t('Ergebnis melden', 'Report the result')}</button>;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="text-[0.8125rem] text-text-hell">{t('Im Markt:', 'In the market:')}</span>
      {!e.zaehlbar ? <span title={e.grund}><Marke>{t('zählt nicht', 'does not count')}</Marke></span>
        : e.treffer ? <span title={e.grund}><Marke art="gut">{t('Treffer', 'hit')}</Marke></span>
        : <span title={e.grund}><Marke art="schlecht">{t('daneben', 'miss')}</Marke></span>}
      <button type="button" onClick={() => melden(l)} className={leise}>{t('ändern', 'change')}</button>
    </span>
  );
}

/**
 * Die Fassungen einer Gruppe nebeneinander, älteste zuerst. Zeigt, was die
 * flache Liste versteckte: wie sich beide Scores von Fassung zu Fassung
 * bewegen. Die jüngste ist hervorgehoben, weil man an ihr weiterarbeitet.
 */
function Verlauf({ laeufe }: { laeufe: Lauf[] }) {
  return (
    <div className="grid grid-cols-3 gap-2 px-4 pb-4 sm:grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] sm:gap-2.5 sm:px-5">
      {laeufe.map((l, i) => {
        const letzte = i === laeufe.length - 1;
        return (
          <Link
            key={l.id}
            to={`/runs/${encodeURIComponent(l.id)}`}
            className={`block min-w-0 rounded-lg border px-2.5 py-2 no-underline transition-colors duration-150 hover:border-marke sm:px-3 sm:py-2.5 ${letzte ? 'border-marke/45 bg-flaeche' : 'border-linie-zart bg-ebene'}`}
          >
            <span className="flex items-baseline justify-between text-[0.75rem] text-text-hell">
              <span className="font-mono font-semibold">F{i + 1}</span>
              <span className="tabular-nums">{uhrzeit(l.created_at)}</span>
            </span>
            <span className="my-1.5 flex h-9 items-end gap-1 sm:my-2 sm:h-14 sm:gap-1.5" aria-hidden>
              <span className="flex-1 rounded-t bg-marke" style={{ height: `${aufSkala(l.scores!.candidate)}%` }} />
              <span className="flex-1 rounded-t bg-linie-stark" style={{ height: `${aufSkala(l.scores!.baseline)}%` }} />
            </span>
            <span className="block text-[0.75rem] whitespace-nowrap tabular-nums text-text-leise sm:text-[0.8125rem]">
              <span className="font-semibold text-marke-tief">{zahl(l.scores!.candidate)}</span> {t('zu', 'to')} {zahl(l.scores!.baseline)}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

/** Eine Zeile in der Gruppe. Schmal gestapelt, breit als Raster mit festen Spalten. */
function RunZeile({ l, nr, melden }: { l: Lauf; nr: number | null; melden: (l: Lauf) => void }) {
  return (
    <li className="flex flex-col gap-2 border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:grid sm:grid-cols-[2rem_minmax(0,1fr)_7rem_11rem_5.5rem] sm:items-start sm:gap-4 sm:px-5">
      <span className="hidden pt-0.5 font-mono text-[0.75rem] font-semibold text-text-hell sm:block">{nr ? `F${nr}` : ''}</span>
      <div className="order-2 min-w-0 sm:order-none">
        <span className="text-[0.9375rem]"><Titel l={l} /></span>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem] text-text-hell">
          <span className="min-w-0"><Meta teile={[l.text ? fassungsname(l.variant) : null, uhrzeit(l.created_at)]} /></span>
          <Markt l={l} melden={melden} />
        </div>
      </div>
      <div className="order-1 flex items-center justify-between gap-3 sm:order-none sm:block">
        <Urteil l={l} />
        <span className="text-[0.8125rem] tabular-nums text-text-leise sm:hidden">{t(`${l.credits} Credits`, `${l.credits} ${l.credits === 1 ? 'credit' : 'credits'}`)}</span>
      </div>
      <div className="order-3 sm:order-none"><Skala l={l} /></div>
      <div className="hidden text-right text-[0.8125rem] tabular-nums text-text-hell sm:block">
        <span className="block font-medium text-text">{t(`${l.credits} Credits`, `${l.credits} ${l.credits === 1 ? 'credit' : 'credits'}`)}</span>
        {t(`${l.personas} Personas`, `${l.personas} ${l.personas === 1 ? 'persona' : 'personas'}`)}
      </div>
    </li>
  );
}

function GruppenKarte({ g, melden }: { g: RunGruppe; melden: (l: Lauf) => void }) {
  const nummer = new Map(g.verlauf.map((l, i) => [l.id, i + 1]));
  return (
    <Panel className="overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 pt-4 pb-3.5 sm:px-5">
        <div className="min-w-0">
          <div className="text-[0.6875rem] font-semibold tracking-[0.07em] text-text-hell uppercase">{g.etikett.join(' · ')}</div>
          <h3 className="m-0 mt-1 font-serif text-[1.25rem] leading-tight font-semibold text-tinte">
            {g.zitat ? t(`„${g.titel}“`, `“${g.titel}”`) : g.titel}
          </h3>
        </div>
        <span className="text-[0.8125rem] tabular-nums text-text-hell">
          {g.laeufe.length} {g.laeufe.length === 1 ? t('Run', 'run') : t('Runs', 'runs')} · {t(`${g.credits} Credits`, `${g.credits} ${g.credits === 1 ? 'credit' : 'credits'}`)}
        </span>
      </div>
      {g.verlauf.length > 0 && <Verlauf laeufe={g.verlauf} />}
      <ul className="m-0 list-none border-t border-linie-zart p-0">
        {g.laeufe.map(l => <RunZeile key={l.id} l={l} nr={nummer.get(l.id) ?? null} melden={melden} />)}
      </ul>
    </Panel>
  );
}

/**
 * Ein Run, der allein in seiner Gruppe steht, als kompakte Zeile (Entwurf 3b,
 * „Feinschliff Oktober", gewaehlt von Robert am 04.10.2026). Vorher bekam
 * jeder eine eigene Karte mit Kopf und einer Zeile: dreimal die Hoehe fuer
 * dieselbe Auskunft, und der Titel stand oft doppelt da.
 */
function EinzelZeile({ l, melden }: { l: Lauf; melden: (l: Lauf) => void }) {
  return (
    <li className="flex flex-col gap-1.5 border-t border-linie-zart px-4 py-3 first:border-t-0 sm:grid sm:grid-cols-[4.5rem_minmax(0,1fr)_8rem_5.5rem] sm:items-center sm:gap-4 sm:px-5">
      <div className="flex items-center justify-between gap-3 sm:contents">
        <span className="text-[0.75rem] text-text-hell tabular-nums sm:order-1">{tagText(l.created_at)}</span>
        <span className="text-[0.8125rem] text-text-leise tabular-nums sm:order-4 sm:text-right">{t(`${l.credits} Credits`, `${l.credits} ${l.credits === 1 ? 'credit' : 'credits'}`)}</span>
      </div>
      <div className="min-w-0 sm:order-2">
        <span className="text-[0.9375rem]"><Titel l={l} /></span>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem] text-text-hell">
          <span className="min-w-0"><Meta teile={[textart()[l.asset] ?? l.asset, l.set_name || l.set, l.text ? fassungsname(l.variant) : null, uhrzeit(l.created_at)]} /></span>
          <Markt l={l} melden={melden} />
        </div>
      </div>
      <div className="sm:order-3"><Urteil l={l} /></div>
    </li>
  );
}

/**
 * Set, Asset, Studie: umbrechen nur zwischen den Teilen, nie in einem Teil.
 * „Demo-Seite / Q4" in zwei Zeilen liest sich wie zwei Angaben. Ein Teil, der
 * allein breiter ist als die Spalte, wird gekuerzt statt die Seite zu sprengen.
 */
function Meta({ teile }: { teile: Array<string | null | undefined> }) {
  const da = teile.filter((t): t is string => !!t);
  return (
    <>
      {da.map((t, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          <span title={t} className="inline-block max-w-full truncate align-bottom whitespace-nowrap">{t}</span>
        </span>
      ))}
    </>
  );
}

/**
 * Was die Zeile heißt. Anlass 29.09.2026 (Robert): „sehe die Headline nicht".
 * Vorher stand hier nur der Fassungsname, den Claude vergibt (f1-alter-neuer-weg),
 * und darunter der Slug der Zielgruppe. Jetzt: der geprüfte Text selbst, darunter
 * Fassungsname, Name der Zielgruppe und Textart.
 */
function titelVon(l: Lauf): { text: string; zitat: boolean } {
  if (l.text) return { text: l.text, zitat: true };
  if (l.mode === 'vorauswahl') return { text: t('Vorauswahl mehrerer Fassungen', 'Preselection of several versions'), zitat: false };
  return { text: fassungsname(l.variant) || t('Run ohne Namen', 'Unnamed run'), zitat: false };
}

function metaVon(l: Lauf, mitZeit = false): Array<string | null | undefined> {
  return [l.text ? fassungsname(l.variant) : null, l.set_name || l.set, textart()[l.asset] ?? l.asset, l.study, mitZeit ? zeitpunkt(l.created_at) : null];
}

function Titel({ l }: { l: Lauf }) {
  const ti = titelVon(l);
  return (
    <Link to={`/runs/${encodeURIComponent(l.id)}`} className={`leading-snug no-underline hover:text-marke-tief hover:underline hover:decoration-marke hover:underline-offset-[3px] ${ti.zitat ? 'font-medium text-tinte' : 'text-text-leise'}`}>
      {ti.zitat ? t(`„${ti.text}“`, `“${ti.text}”`) : ti.text}
    </Link>
  );
}

const zahl = (n: number) => new Intl.NumberFormat(locale(), { maximumFractionDigits: 1 }).format(n);

const zeitpunkt = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: '2-digit' })
      + ', ' + d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
};

/** Nur die Uhrzeit: den Tag nennt die Gruppe. */
const uhrzeit = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
};

export default function Laeufe() {
  const { session, abmelden } = useSitzung();
  const [laeufe, setLaeufe] = useState<Lauf[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [nochmal, setNochmal] = useState(true);
  const [laedt, setLaedt] = useState(true);
  const [quote, setQuote] = useState<Quote | null>(null);
  // Nur fuer die leere Liste: ist das Konto schon mit Claude verbunden?
  const [verbunden, setVerbunden] = useState(false);
  const [offen, setOffen] = useState<Lauf | null>(null);
  const [params, setParams] = useSearchParams();
  const [suche, setSuche] = useState('');
  const [antwort, setAntwort] = useState<{ grund: string; zaehlbar: boolean; quote: string } | null>(null);
  const formular = useRef<HTMLElement>(null);
  // Quoten der anderen Zielgruppenarten. Sie zählen getrennt, weil die Jury
  // dort in einer anderen Rolle urteilt (supabase/functions/_shared/sim/jury.ts).
  const [weitere, setWeitere] = useState<NonNullable<Trefferquoten['weitere']>>([]);

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      const liste = await ladeLaeufe();
      setLaeufe(liste);
      // Ohne Lauf zeigt die Seite die drei Schritte. „Mit Claude verbinden"
      // ist erledigt, sobald der erste Aufruf aus Claude kam (billing/status,
      // Regel in verbindung.ts). Scheitert der Status, bleibt der Schritt offen.
      if (liste.length === 0) {
        void ladeStatus().then(st => { setVerbunden(istVerbunden(st)); meldeVerbindung(st); }, e => console.warn('[laeufe] Status', e));
      }
      // Die Quote ist eine Beigabe: fehlt sie, bleibt die Liste benutzbar.
      const q = await ladeQuote().catch(e => { console.warn('[laeufe] Quote', e); return null; });
      setQuote(q?.eigene ?? null);
      setWeitere(q?.weitere ?? []);
    } catch (e) {
      const f = e as ApiFehler;
      setFehler(f.message);
      // Gesperrtes Konto: ein zweiter Versuch aendert nichts, also kein Knopf dafuer.
      setNochmal(nochmalSinnvoll(f));
      if (f.abgemeldet) void abmelden();
    } finally {
      setLaedt(false);
    }
  }, [abmelden]);

  useEffect(() => { void holen(); }, [holen]);
  // Laeufe entstehen in Claude: beim Zurueckkehren in den Tab neu laden
  // (neu-laden.ts). Ersetzt den Knopf „Aktualisieren".
  useNeuLadenBeimZurueckkehren(() => void holen());
  useEffect(() => { if (offen) formular.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [offen]);

  const melden = (l: Lauf) => { setAntwort(null); setOffen(l); };
  const wartend = laeufe?.filter(l => l.mode === 'compare' && !l.ergebnis) ?? [];
  const ohneErgebnis = wartend.length;
  // Der Knopf im Kasten öffnet den jüngsten Vergleich ohne Ergebnis. Das
  // Formular nennt Text und Zeitpunkt, man sieht also, welcher es ist.
  const juengsterWartend = [...wartend].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  // Projekte und Suche (runs-projekte.ts). Das Projekt steht in der Adresse,
  // damit ein Zurück aus einem Run dort landet, wo man war.
  const uebersicht = projekteAus(laeufe ?? []);
  const wahl = gueltigeWahl(wahlAusParam(params.get('projekt')), uebersicht);
  const waehle = (w: ProjektWahl) => {
    const neu = new URLSearchParams(params);
    const v = wahlAlsParam(w);
    if (v === null) neu.delete('projekt'); else neu.set('projekt', v);
    setParams(neu, { replace: true });
  };
  const sichtbar = laeufe ? filtere(laeufe, wahl, suche) : null;
  const gefiltert = !!laeufe && !!sichtbar && sichtbar.length !== laeufe.length;
  const ohneProjektStudien = laeufe && wahl.art === 'ohne' ? studienOhneProjekt(laeufe) : [];
  const gruppen = sichtbar ? gruppiere(sichtbar, textart()) : [];
  // Runs allein in ihrer Gruppe: kompakt in einer Karte unter den Gruppen mit Verlauf.
  const einzelne = gruppen.filter(g => g.laeufe.length === 1).map(g => g.laeufe[0]!);
  const zahlSichtbar = sichtbar?.length ?? 0;

  return (
    <AppRahmen email={session?.user?.email ?? ''} abmelden={() => void abmelden()}>
      <Seitenkopf
        titel={t('Runs', 'Runs')}
        unter={t('Jeder Run aus Claude mit Urteil und Kosten. Ein Klick zeigt, was die Personas gesagt haben.', 'Every run from Claude with verdict and cost. One click shows what the personas said.')}
        aktion={laeufe && laeufe.length > 0
          ? <span className="text-[0.8125rem] tabular-nums text-text-hell">{t(`${laeufe.length} Runs · ${laeufe.reduce((s, l) => s + l.credits, 0)} Credits in Runs`, `${laeufe.length} ${laeufe.length === 1 ? 'run' : 'runs'} · ${laeufe.reduce((s, l) => s + l.credits, 0)} credits in runs`)}</span>
          : undefined}
      />

      {fehler && <Hinweis art="schlecht">{fehler}{nochmal && <> <button type="button" onClick={() => void holen()} className="cursor-pointer border-0 bg-transparent p-0 font-medium text-current underline underline-offset-[3px]">{t('Noch einmal laden', 'Load again')}</button></>}</Hinweis>}

      {/* Die Quote als eine Zeile ueber der Liste: sie ist die Frage, fuer die
          man Ergebnisse meldet, und steht deshalb vor den Knoepfen dazu. */}
      {laeufe?.some(l => l.mode === 'compare') && (
        <Panel className="mb-8 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 px-4 py-4 sm:px-5">
          <div className="min-w-0 flex-[1_1_20rem]">
            <h2 className="m-0 text-[0.8125rem] font-medium text-text-leise">{t('Hatte die Simulation recht?', 'Was the simulation right?')}</h2>
            {/* Anlass 29.09.2026 (Robert): „was bedeutet Ergebnis melden?" Solange
                nichts gemeldet ist, steht hier die Erklärung statt „noch keine Quote". */}
            {!quote || quote.n + quote.nicht_zaehlbar === 0 ? (
              <p className="mt-0.5 mb-0 max-w-[68ch] text-[0.9375rem] leading-relaxed text-text">
                {t(
                  <>Lief eine geprüfte Fassung live, in einem Google-Ads-Experiment oder einem A/B-Test mit fester Aufteilung, melde, welche im Markt gewonnen hat,
                  am besten mit Klicks oder Conversions je Fassung. Daraus misst 99takes, wie oft die Simulation richtig lag.</>,
                  <>If a tested version went live, in a Google Ads experiment or an A/B test with a fixed split, report which one won in the market,
                  ideally with clicks or conversions per version. From that, 99takes measures how often the simulation was right.</>,
                )}
              </p>
            ) : (
              <p className="mt-0.5 mb-0 text-[0.9375rem] leading-relaxed text-text">
                {weitere.length > 0 && <span className="font-medium">{t('Firmenkunden: ', 'Business customers: ')}</span>}
                {quote.satz}
              </p>
            )}
            {weitere.map(w => (
              <p key={w.zielgruppe_art} className="mt-1 mb-0 text-[0.9375rem] leading-relaxed text-text">
                <span className="font-medium">{w.zielgruppe_art === 'b2c' ? t('Privatkunden', 'Consumers') : w.zielgruppe_art === 'politik' ? t('Politik', 'Politics') : t('Firmenkunden', 'Business customers')}: </span>
                {w.eigene.satz}
              </p>
            ))}
          </div>
          {juengsterWartend && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <p className="m-0 flex items-baseline gap-2 text-[0.8125rem] leading-snug text-text-hell">
                <span className="font-serif text-[1.625rem] leading-none font-semibold tabular-nums text-marke-tief">{ohneErgebnis}</span>
                <span>{t(<>{ohneErgebnis === 1 ? 'Vergleich wartet' : 'Vergleiche warten'} auf ein<br />Marktergebnis. Kostet nichts.</>, <>{ohneErgebnis === 1 ? 'comparison is waiting' : 'comparisons are waiting'} for a<br />market result. Free.</>)}</span>
              </p>
              <button
                type="button"
                onClick={() => melden(juengsterWartend)}
                className="inline-flex min-h-9 cursor-pointer items-center rounded-lg border-0 bg-knopf px-3.5 text-[0.875rem] font-semibold whitespace-nowrap text-auf-marke hover:bg-knopf-hover"
              >
                {t('Ergebnis melden', 'Report the result')}
              </button>
            </div>
          )}
        </Panel>
      )}

      {antwort && (
        <Hinweis art={antwort.zaehlbar ? 'gut' : 'info'}>
          {antwort.grund} {antwort.quote}
        </Hinweis>
      )}

      {offen && (
        <section ref={formular} className="mb-8 scroll-mt-4 rounded-karte border border-marke/40 bg-flaeche p-4 sm:p-6">
          <h2 className="m-0 mb-1 text-base font-semibold tracking-[-0.01em] text-tinte">
            {t('Ergebnis melden:', 'Report the result:')} {titelVon(offen).zitat ? t(`„${titelVon(offen).text}“`, `“${titelVon(offen).text}”`) : titelVon(offen).text} {t('vom', 'from')} {zeitpunkt(offen.created_at)}
          </h2>
          <p className="mt-0 mb-4 text-[0.8125rem] break-words text-text-hell">{metaVon(offen).filter(Boolean).join(' · ')}</p>
          <ErgebnisMelden
            lauf={offen}
            abbrechen={() => setOffen(null)}
            fertig={a => { setOffen(null); setAntwort(a); void holen(); }}
          />
        </section>
      )}

      <Abschnitt
        titel={laeufe && laeufe.length > 0 ? (wahl.art === 'projekt' ? wahl.name : wahl.art === 'ohne' ? t('Ohne Projekt', 'No project') : t('Letzte Runs', 'Recent runs')) : laeufe?.length === 0 ? t('Dein erster Run', 'Your first run') : t('Runs', 'Runs')}
        zahl={laeufe && laeufe.length > 0 ? zahlSichtbar : laeufe?.length}
        unter={laeufe?.length === 0 ? t('Geprüft wird in Claude. Hier landet danach jeder Run mit Urteil und Kosten.', 'Testing happens in Claude. Every run then lands here with verdict and cost.') : undefined}
        aktion={laeufe && laeufe.length > 0 && laeufe.some(l => l.scores) ? (
          <span className="flex items-center gap-3.5 text-[0.75rem] text-text-hell">
            <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-marke" />{t('deine Fassung', 'your version')}</span>
            <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full border-2 border-text-leise bg-flaeche" />{t('Vergleich', 'baseline')}</span>
            <span>{t('Score 0 bis 10', 'Score 0 to 10')}</span>
          </span>
        ) : undefined}
      >
        {laedt && !laeufe && (
          <Panel>
            {[0, 1, 2].map(i => <Zeile key={i}><Platzhalter zeilen={2} /></Zeile>)}
          </Panel>
        )}

        {!laedt && !laeufe && fehler && (
          <Panel className="px-4 py-5 sm:px-5">
            <p className="m-0 text-[0.9375rem] leading-relaxed text-text-leise">
              {t('Die Liste ließ sich nicht laden.', 'The list could not be loaded.')}
            </p>
            {nochmal && <KleinerKnopf className="mt-3" onClick={() => void holen()} disabled={laedt}>{laedt ? t('Lädt …', 'Loading …') : t('Noch einmal laden', 'Load again')}</KleinerKnopf>}
          </Panel>
        )}

        {laeufe?.length === 0 && (() => {
          const antworten = bekannteAntworten(session?.user?.id ?? session?.user?.email);
          return <ErsterLauf erledigt={verbunden ? ['verbunden'] : []} vorschlag={ersterLaufVorschlag(antworten)} passend={!!antworten && Object.keys(antworten).length > 0} />;
        })()}

        {laeufe && laeufe.length > 0 && (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
          <ProjektNavigation u={uebersicht} wahl={wahl} waehle={waehle} />
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <RunSuche
              wert={suche}
              setzen={setSuche}
              platzhalter={wahl.art === 'projekt' ? t(`In ${wahl.name} suchen`, `Search in ${wahl.name}`) : t('Studie, Projekt oder Text', 'Study, project or text')}
            />
            {wahl.art === 'ohne' && (
              <ProjekteZuordnen studien={ohneProjektStudien} projekte={uebersicht.projekte.map(p => p.name)} fertig={() => void holen()} />
            )}
            {gefiltert && zahlSichtbar === 0 && (
              <Panel className="px-4 py-5 sm:px-5">
                <p className="m-0 text-[0.9375rem] leading-relaxed text-text-leise">{t('Keine Runs passen dazu.', 'No runs match.')}</p>
                <KleinerKnopf className="mt-3" onClick={() => { setSuche(''); waehle({ art: 'alle' }); }}>{t('Alle Runs zeigen', 'Show all runs')}</KleinerKnopf>
              </Panel>
            )}
            {gruppen.filter(g => g.laeufe.length > 1).map(g => <GruppenKarte key={g.schluessel} g={g} melden={melden} />)}
            {einzelne.length > 0 && (
              <div>
                {einzelne.length < zahlSichtbar && (
                  <h3 className="m-0 mt-4 mb-2.5 text-[0.75rem] font-semibold tracking-[0.07em] text-text-leise uppercase">{t('Einzelne Runs', 'Single runs')}</h3>
                )}
                <Panel>
                  <ul className="m-0 list-none p-0">
                    {einzelne.map(l => <EinzelZeile key={l.id} l={l} melden={melden} />)}
                  </ul>
                </Panel>
              </div>
            )}
          </div>
          </div>
        )}

        {laeufe?.some(l => l.mode === 'compare') && (
          <details className="group mt-3 [&_summary::-webkit-details-marker]:hidden">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[0.875rem] font-medium text-tinte hover:text-marke-tief">
              <span aria-hidden className="inline-block text-text-hell transition-transform duration-200 group-open:rotate-90">▸</span>
              {t('So liest du das Urteil', 'How to read the verdict')}
            </summary>
            <div className="mt-3 max-w-[72ch] rounded-karte border border-linie bg-flaeche shadow-flaeche p-4 sm:p-5">
              <dl className="m-0 grid gap-x-4 gap-y-3 text-[0.875rem] leading-relaxed sm:grid-cols-[auto_1fr]">
                <dt><Marke art="gut">{t('gewinnt', 'wins')}</Marke></dt>
                <dd className="m-0 text-text-leise">
                  {t(
                    <>Mehr Personas ziehen deinen Text der Vergleichsfassung vor, und zwar deutlicher,
                    als derselbe Vergleich bei Wiederholung schwankt.</>,
                    <>More personas prefer your copy to the baseline, and by more than
                    the same comparison varies when repeated.</>,
                  )}
                </dd>
                <dt><Marke art="schlecht">{t('verliert', 'loses')}</Marke></dt>
                <dd className="m-0 text-text-leise">{t('Dasselbe umgekehrt: die Vergleichsfassung trägt besser.', 'The same the other way round: the baseline works better.')}</dd>
                <dt><Marke>{t('zu knapp', 'too close')}</Marke></dt>
                <dd className="m-0 text-text-leise">
                  {t(
                    <>Der Vorsprung liegt im Rauschen, bei 48 Einzelurteilen etwa 32:16. Das ist kein
                    Fehler, sondern eine Aussage: auf dieser Zahl solltest du nicht entscheiden.
                    Entscheide auf Handwerk, oder lass den Vergleich in Claude mehrfach laufen.</>,
                    <>The lead is within the noise, with 48 single verdicts about 32:16. That is not an
                    error but a finding: you should not decide on this number.
                    Decide on craft, or run the comparison several times in Claude.</>,
                  )}
                </dd>
              </dl>
              <p className="mt-4 mb-0 border-t border-linie-zart pt-3 text-[0.8125rem] leading-relaxed text-text-hell">
                {t(
                  <>Die Grenze ist gemessen, nicht geschätzt: derselbe Vergleich lief dreimal und schwankte
                  zwischen deutlichem Sieg und Patt. Ein Vorsprung bis zu einem Drittel der Personas ist
                  deshalb kein Ergebnis, eine Punktdifferenz unter 0,6 auch nicht.</>,
                  <>The threshold is measured, not estimated: the same comparison ran three times and swung
                  between a clear win and a draw. A lead of up to a third of the personas is
                  therefore not a result, and neither is a points difference below 0.6.</>,
                )}
              </p>
              <p className="mt-3 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
                {t(
                  <>Runs vom selben Tag mit derselben Studie, Zielgruppe und Textart stehen in einer Karte,
                  ihre Fassungen F1, F2, … im Verlauf darüber.</>,
                  <>Runs from the same day with the same study, audience and copy type share one card,
                  with their versions F1, F2, … in the history above.</>,
                )}
              </p>
            </div>
          </details>
        )}

      </Abschnitt>

      <Berichte />
    </AppRahmen>
  );
}

/**
 * Die Berichte des Kontos. Bis zum 25.09.2026 war ein Bericht ohne Link
 * nirgends einsehbar, und ein freigegebener Link liess sich nicht
 * zurueckziehen. Wer seine Persona-Recherche per Link teilt, muss ihn hier
 * auch wieder schliessen koennen — sonst waere die Zustimmung eine Einbahnstrasse.
 */
function Berichte() {
  const [liste, setListe] = useState<BerichtEintrag[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [arbeitet, setArbeitet] = useState('');
  const [kopiert, setKopiert] = useState('');
  // Zugang: welcher Bericht gerade bearbeitet wird und der Entwurf der Liste.
  const [zugangFuer, setZugangFuer] = useState('');
  const [entwurf, setEntwurf] = useState('');
  useEffect(() => {
    if (!kopiert) return;
    const t = setTimeout(() => setKopiert(''), 2000);
    return () => clearTimeout(t);
  }, [kopiert]);

  const holen = useCallback(async () => {
    setFehler('');
    try {
      setListe(await ladeBerichte());
    } catch (e) {
      setFehler((e as Error).message);
    }
  }, []);
  useEffect(() => { void holen(); }, [holen]);
  useNeuLadenBeimZurueckkehren(() => void holen());

  async function zurueckziehen(b: BerichtEintrag) {
    const sicher = window.confirm(t(
      `Link zum Bericht „${b.study ?? 'ohne Namen'}" zurückziehen?\n\n`
      + 'Wer ihn hat, sieht danach nur noch „nicht verfügbar". Das lässt sich nicht rückgängig machen — '
      + 'für einen neuen Link den Bericht in Claude erneut veröffentlichen.',
      `Withdraw the link to the report “${b.study ?? 'unnamed'}”?\n\n`
      + 'Anyone who has it will then only see “not available”. This cannot be undone. '
      + 'For a new link, publish the report again in Claude.',
    ));
    if (!sicher) return;
    setArbeitet(b.id);
    try {
      await zieheLinkZurueck(b.id);
      await holen();
    } catch (e) {
      setFehler(t(`Der Link ließ sich nicht zurückziehen: ${(e as Error).message}`, `The link could not be withdrawn: ${(e as Error).message}`));
    } finally {
      setArbeitet('');
    }
  }

  function zugangOeffnen(b: BerichtEintrag) {
    setFehler('');
    setZugangFuer(zugangFuer === b.id ? '' : b.id);
    setEntwurf((b.leser ?? []).join('\n'));
  }

  async function zugangSpeichern(b: BerichtEintrag) {
    const leser = entwurf.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
    setArbeitet(b.id);
    setFehler('');
    try {
      await setzeLeser(b.id, leser);
      setZugangFuer('');
      await holen();
    } catch (e) {
      setFehler(t(`Der Zugang ließ sich nicht ändern: ${(e as Error).message}`, `Access could not be changed: ${(e as Error).message}`));
    } finally {
      setArbeitet('');
    }
  }

  return (
    <Abschnitt
      titel={t('Berichte', 'Reports')}
      zahl={liste?.length}
      unter={t(<>Eingefrorene Stände einer Studie. Einen neuen erzeugst du in Claude: „Mach aus der Studie einen Bericht“.</>, <>Frozen snapshots of a study. You create a new one in Claude: “Turn the study into a report”.</>)}
    >
      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
      {!liste && !fehler && (
        <Panel>
          {[0, 1].map(i => <Zeile key={i}><Platzhalter zeilen={2} /></Zeile>)}
        </Panel>
      )}
      {liste?.length === 0 && (
        <Panel className="px-4 py-5 sm:px-5">
          <p className="m-0 text-[0.9375rem] text-text-leise">{t('Noch kein Bericht.', 'No report yet.')}</p>
        </Panel>
      )}
      {liste && liste.length > 0 && (
        <Panel>
          <ul className="m-0 list-none p-0">
            {liste.map(b => (
              <li key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:px-5">
                <div className="min-w-0 flex-[1_1_16rem]">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className={`text-[0.9375rem] font-semibold ${b.study ? 'text-tinte' : 'text-text-hell'}`}>{b.study ?? t('Ohne Namen', 'Unnamed')}</span>
                    {b.shared && b.leser?.length
                      ? <Marke art="marke">{b.leser.length === 1 ? t('nur für 1 Adresse', 'only for 1 address') : t(`nur für ${b.leser.length} Adressen`, `only for ${b.leser.length} addresses`)}</Marke>
                      : b.shared
                      ? <Marke art="marke">{t('Link offen', 'Link open')}</Marke>
                      : b.revoked_at ? <Marke>{t('Link zurückgezogen', 'Link withdrawn')}</Marke> : <Marke>{t('nur für dich', 'only for you')}</Marke>}
                    {/* Zurueckziehen steht am Status, den es aendert, als leiser
                        Textknopf: so passen die zwei Knoepfe darunter auch bei
                        340 px in eine Zeile, und die unumkehrbare Aktion steht
                        nicht gleichrangig neben „Öffnen". */}
                    {b.url && (
                      <button
                        type="button" disabled={arbeitet === b.id} onClick={() => void zurueckziehen(b)} aria-label={t(`Link zu „${b.study ?? 'ohne Namen'}“ zurückziehen`, `Withdraw link to “${b.study ?? 'unnamed'}”`)}
                        className="cursor-pointer border-0 bg-transparent p-0 text-[0.8125rem] text-text-hell underline underline-offset-[3px] hover:text-schlecht disabled:cursor-wait"
                      >
                        {arbeitet === b.id ? t('wird zurückgezogen …', 'withdrawing …') : t('zurückziehen', 'withdraw')}
                      </button>
                    )}
                  </div>
                  <div className="mt-0.5 text-[0.8125rem] [overflow-wrap:anywhere] text-text-hell">
                    {zeitpunkt(b.created_at)} · {b.variants} {b.variants === 1 ? t('Fassung', 'version') : t('Fassungen', 'versions')}
                    {b.set ? <> · {b.set}</> : null}
                    {b.shared && b.shared_by_owner ? <> · {t('zeigt deine Persona-Recherche samt Belegen', 'shows your persona research with evidence')}</> : null}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to={`/report?id=${encodeURIComponent(b.id)}`}
                    className="inline-flex min-h-8 items-center rounded-md border border-linie bg-flaeche px-3 py-1.5 text-[0.8125rem] font-medium text-text no-underline hover:border-marke hover:text-marke-tief"
                  >
                    {t('Öffnen', 'Open')}
                  </Link>
                  {b.url && (
                    <KleinerKnopf type="button" onClick={() => void navigator.clipboard.writeText(b.url!).then(() => setKopiert(b.id), e => console.warn('[kopieren]', e))}>
                      <span aria-live="polite">{kopiert === b.id ? t('Kopiert', 'Copied') : t('Link kopieren', 'Copy link')}</span>
                    </KleinerKnopf>
                  )}
                  {b.url && (
                    <KleinerKnopf type="button" aria-expanded={zugangFuer === b.id} onClick={() => zugangOeffnen(b)}>
                      {t('Wer darf lesen', 'Who can read')}
                    </KleinerKnopf>
                  )}
                </div>
                {zugangFuer === b.id && (
                  <form
                    className="basis-full"
                    onSubmit={e => { e.preventDefault(); void zugangSpeichern(b); }}
                  >
                    <label htmlFor={`leser-${b.id}`} className="block text-[0.8125rem] font-medium text-text">
                      {t('Nur diese E-Mail-Adressen', 'Only these email addresses')}
                    </label>
                    <textarea
                      id={`leser-${b.id}`} rows={3} value={entwurf} onChange={e => setEntwurf(e.target.value)}
                      placeholder={'yves@kunde.com\nnora@kunde.com'} spellCheck={false} autoComplete="off"
                      className="mt-1 block w-full max-w-[32rem] rounded-md border border-linie bg-flaeche px-3 py-2 font-mono text-[0.8125rem] text-text"
                    />
                    <p className="mt-1 mb-2 max-w-[32rem] text-[0.8125rem] leading-snug text-text-hell">
                      {t('Wer den Link öffnet, gibt seine Adresse ein und bekommt einen Code per Mail. Leer lassen: jeder mit dem Link kann lesen.', 'Whoever opens the link enters their address and gets a code by email. Leave it empty: anyone with the link can read.')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <KleinerKnopf type="submit" disabled={arbeitet === b.id}>{arbeitet === b.id ? t('Speichert …', 'Saving …') : t('Speichern', 'Save')}</KleinerKnopf>
                      <KleinerKnopf type="button" onClick={() => setZugangFuer('')}>{t('Abbrechen', 'Cancel')}</KleinerKnopf>
                    </div>
                  </form>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </Abschnitt>
  );
}
